// @ts-nocheck
/**
 * ドラフトシステム - NPCピックAI
 */

import { getNPCInfo, RARITY_SCORES } from '../data/npcPersonalities';

// ========================================
// コスト帯判定
// ========================================

/**
 * コスト帯を判定
 * @param {number} cost - カードコスト
 * @returns {string} 'lowCost' | 'midCost' | 'highCost'
 */
const getCostBand = (cost) => {
  if (cost <= 2) return 'lowCost';
  if (cost <= 4) return 'midCost';
  return 'highCost';
};

/**
 * 現在のコスト曲線を分析
 * @param {Object[]} pickedCards - ピック済みカード
 * @returns {Object} コスト帯ごとの比率
 */
const analyzeCurrentCurve = (pickedCards) => {
  const total = pickedCards.length || 1;
  const counts = { lowCost: 0, midCost: 0, highCost: 0 };

  pickedCards.forEach(card => {
    const cost = card.cost || 0;
    counts[getCostBand(cost)]++;
  });

  return {
    lowCost: counts.lowCost / total,
    midCost: counts.midCost / total,
    highCost: counts.highCost / total,
  };
};

// ========================================
// シナジー計算
// ========================================

/**
 * シナジーを計算
 * @param {Object} card - 評価対象カード
 * @param {Object[]} pickedCards - ピック済みカード
 * @returns {number} シナジースコア（0-1）
 */
const calculateSynergy = (card, pickedCards) => {
  if (pickedCards.length === 0) return 0;

  let synergy = 0;

  // 同属性ボーナス
  const sameAttribute = pickedCards.filter(p => p.attribute === card.attribute).length;
  synergy += sameAttribute * 0.1;

  // 同カテゴリボーナス
  if (card.category) {
    const categories = Array.isArray(card.category) ? card.category : [card.category];
    categories.forEach(cat => {
      // カテゴリ文字列から【】を除去して比較
      const catName = cat.replace(/【|】/g, '');
      const sameCategory = pickedCards.filter(p => {
        if (!p.category) return false;
        const pCategories = Array.isArray(p.category) ? p.category : [p.category];
        return pCategories.some(pc => pc.includes(catName));
      }).length;
      synergy += sameCategory * 0.15;
    });
  }

  return Math.min(synergy, 1.0);
};

/**
 * 相手が欲しそうなカードかを推定
 * @param {Object} card - 評価対象カード
 * @param {Object[][]} otherPlayerPicks - 他プレイヤーのピック
 * @returns {number} 欲求度スコア
 */
const estimateOpponentDesire = (card, otherPlayerPicks) => {
  let desire = 0;

  otherPlayerPicks.forEach(picks => {
    // 同属性が多いプレイヤーは欲しがりそう
    const sameAttr = picks.filter(p => p.attribute === card.attribute).length;
    if (sameAttr >= 3) {
      desire += 0.3;
    }

    // 同カテゴリが多いプレイヤーは欲しがりそう
    if (card.category) {
      const categories = Array.isArray(card.category) ? card.category : [card.category];
      categories.forEach(cat => {
        const catName = cat.replace(/【|】/g, '');
        const sameCat = picks.filter(p => {
          if (!p.category) return false;
          const pCategories = Array.isArray(p.category) ? p.category : [p.category];
          return pCategories.some(pc => pc.includes(catName));
        }).length;
        if (sameCat >= 2) {
          desire += 0.2;
        }
      });
    }
  });

  return Math.min(desire, 1.0);
};

// ========================================
// ピックスコア計算
// ========================================

/**
 * カードのピックスコアを計算
 * @param {Object} card - 評価対象カード
 * @param {Object} npc - NPC性格データ
 * @param {Object[]} pickedCards - NPCのピック済みカード
 * @param {Object[][]} otherPlayerPicks - 他プレイヤーのピック（見えている分）
 * @returns {number} スコア
 */
export const calculatePickScore = (card, npc, pickedCards, otherPlayerPicks = []) => {
  let score = 0;

  // 1. 基本価値（レアリティ）
  const rarityScore = RARITY_SCORES[card.rarity] || 10;
  score += rarityScore * npc.rarityWeight;

  // 2. コスト曲線フィット
  const costBand = getCostBand(card.cost || 0);
  const currentCurve = analyzeCurrentCurve(pickedCards);
  const idealRatio = npc.priorityCurve[costBand];
  const currentRatio = currentCurve[costBand];
  const curveFit = Math.max(0, idealRatio - currentRatio);
  score += curveFit * 100;

  // 3. タイプ適合
  if (npc.preferredTypes && npc.preferredTypes.includes(card.type)) {
    score += 25;
  }

  // 4. カテゴリ適合
  if (npc.preferredCategories && card.category) {
    const categories = Array.isArray(card.category) ? card.category : [card.category];
    const hasPreferred = categories.some(cat =>
      npc.preferredCategories.some(pref => cat.includes(pref))
    );
    if (hasPreferred) {
      score += 35;
    }
  }

  // 5. 属性適合
  if (npc.preferredAttributes && npc.preferredAttributes.includes(card.attribute)) {
    score += 30;
  }

  // 6. シナジー評価
  if (npc.synergyWeight > 0 && pickedCards.length > 0) {
    const synergy = calculateSynergy(card, pickedCards);
    score += synergy * npc.synergyWeight * 50;
  }

  // 7. ヘイトドラフト（相手が欲しそうなカードをカット）
  if (Math.random() < npc.hateDraftChance && otherPlayerPicks.length > 0) {
    const opponentDesire = estimateOpponentDesire(card, otherPlayerPicks);
    score += opponentDesire * 25;
  }

  // 8. ランダム要素（同スコア時の差別化）
  score += Math.random() * 5;

  return score;
};

/**
 * NPCがパックから1枚ピック
 * @param {Object[]} packCards - パック内のカード
 * @param {string} personalityId - NPC性格ID
 * @param {Object[]} pickedCards - NPCのピック済みカード
 * @param {Object[][]} otherPlayerPicks - 他プレイヤーのピック
 * @returns {Object} 選ばれたカード
 */
export const npcPickCard = (packCards, personalityId, pickedCards, otherPlayerPicks = []) => {
  if (packCards.length === 0) return null;
  if (packCards.length === 1) return packCards[0];

  const npc = getNPCInfo(personalityId);

  // 各カードのスコアを計算
  const scoredCards = packCards.map(card => ({
    card,
    score: calculatePickScore(card, npc, pickedCards, otherPlayerPicks),
  }));

  // スコア順でソート
  scoredCards.sort((a, b) => b.score - a.score);

  // 最高スコアのカードを返す
  return scoredCards[0].card;
};

export default {
  calculatePickScore,
  npcPickCard,
  getCostBand,
  analyzeCurrentCurve,
};
